import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import {
  calculateAffordabilityScenario,
  exportAffordabilityScenario,
  getAffordabilityBaselineFromTaxHelper,
  getAffordabilityCategories,
} from '../services/api';
import BaselineIncomeConfig from '../components/affordability/BaselineIncomeConfig';
import TemplateIncomeLinker from '../components/affordability/TemplateIncomeLinker';
import CategoryLockSelector from '../components/affordability/CategoryLockSelector';
import ScenarioFactors from '../components/affordability/ScenarioFactors';
import AllocationAdjuster from '../components/affordability/AllocationAdjuster';
import ScenarioComparison from '../components/affordability/ScenarioComparison';
import AIChatButton from '../components/ai/AIChatButton';
import AIChatModal from '../components/ai/AIChatModal';

const steps = [
  'Configure Baseline',
  'Link Templates',
  'Select Categories',
  'Define Scenario',
  'Adjust Allocations',
  'Review Results',
];

function AffordabilityExplorerPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [mobileAIChatOpen, setMobileAIChatOpen] = useState(false);

  // Baseline configuration
  const [baselineState, setBaselineState] = useState('NV');
  const [incomes, setIncomes] = useState([]);
  const [baselineResult, setBaselineResult] = useState(null);

  // Template-income linking (templates inherit frequency from linked income)
  const [templateIncomeMappings, setTemplateIncomeMappings] = useState({});

  // Category configuration
  const [categoriesData, setCategoriesData] = useState([]);
  const [lockedCategoryIds, setLockedCategoryIds] = useState([]);

  // Scenario configuration
  const [scenarioState, setScenarioState] = useState('NV');
  const [factors, setFactors] = useState([]);

  // Allocation overrides
  const [allocationOverrides, setAllocationOverrides] = useState({});

  // Final calculation result
  const [scenarioResult, setScenarioResult] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError('');
    try {
      // Try to get baseline from Tax Helper
      const taxHelperResponse = await getAffordabilityBaselineFromTaxHelper();
      if (taxHelperResponse.data.available) {
        setBaselineState(taxHelperResponse.data.state);
        setScenarioState(taxHelperResponse.data.state);
        setIncomes(taxHelperResponse.data.incomes.map((inc, idx) => ({
          id: idx + 1,
          frequency: 'bi-weekly', // Default frequency
          ...inc
        })));
      } else {
        // Default empty income
        setIncomes([{ id: 1, label: 'Primary Income', gross_annual: 0, type: 'w2', frequency: 'bi-weekly' }]);
      }

      // Load categories
      const categoriesResponse = await getAffordabilityCategories();
      setCategoriesData(categoriesResponse.data.accounts || []);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
      setIncomes([{ id: 1, label: 'Primary Income', gross_annual: 0, type: 'w2', frequency: 'bi-weekly' }]);
    } finally {
      setLoading(false);
    }
  };

  const calculateScenario = useCallback(async () => {
    setCalculating(true);
    setError('');
    try {
      const response = await calculateAffordabilityScenario({
        baseline: {
          state: baselineState,
          incomes: incomes.map(inc => ({
            id: inc.id,
            label: inc.label,
            gross_annual: inc.gross_annual,
            type: inc.type,
            frequency: inc.frequency || 'bi-weekly'
          }))
        },
        scenario: {
          state: scenarioState,
          factors: factors.map(f => ({
            factor_type: f.factor_type,
            label: f.label,
            monthly_amount: f.monthly_amount
          }))
        },
        locked_category_ids: lockedCategoryIds,
        allocation_overrides: allocationOverrides,
        template_income_mappings: templateIncomeMappings
      });
      setScenarioResult(response.data);
      setBaselineResult(response.data.baseline);
    } catch (err) {
      console.error('Scenario calculation error:', err);
      setError(err.response?.data?.msg || 'Failed to calculate scenario');
    } finally {
      setCalculating(false);
    }
  }, [baselineState, incomes, scenarioState, factors, lockedCategoryIds, allocationOverrides, templateIncomeMappings]);

  // Recalculate when relevant inputs change (after step 4 - "Adjust Allocations")
  useEffect(() => {
    if (activeStep >= 4 && incomes.some(i => i.gross_annual > 0)) {
      calculateScenario();
    }
  }, [activeStep, calculateScenario]);

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate baseline has at least one income
      if (!incomes.some(i => i.gross_annual > 0)) {
        setError('Please add at least one income source');
        return;
      }
    }

    // When moving from step 1 (Link Templates) to step 2 (Select Categories),
    // reload categories with the proper frequency mappings
    if (activeStep === 1) {
      setCalculating(true);
      try {
        const categoriesResponse = await getAffordabilityCategories({
          incomes: incomes.map(inc => ({
            id: inc.id,
            label: inc.label,
            gross_annual: inc.gross_annual,
            type: inc.type,
            frequency: inc.frequency || 'bi-weekly'
          })),
          template_income_mappings: templateIncomeMappings
        });
        setCategoriesData(categoriesResponse.data.accounts || []);
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load categories with template mappings');
        setCalculating(false);
        return;
      }
      setCalculating(false);
    }

    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setScenarioState(baselineState);
    setFactors([]);
    setAllocationOverrides({});
    setScenarioResult(null);
  };

  // Export the full report (inputs + computed result + metadata) as a machine-readable JSON file.
  // Posts the live wizard state; the backend owns the versioned schema and stamps metadata.
  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await exportAffordabilityScenario({
        baseline: {
          state: baselineState,
          incomes: incomes.map(inc => ({
            id: inc.id,
            label: inc.label,
            gross_annual: inc.gross_annual,
            type: inc.type,
            frequency: inc.frequency || 'bi-weekly'
          }))
        },
        scenario: {
          state: scenarioState,
          factors: factors.map(f => ({
            factor_type: f.factor_type,
            label: f.label,
            monthly_amount: f.monthly_amount
          }))
        },
        locked_category_ids: lockedCategoryIds,
        allocation_overrides: allocationOverrides,
        template_income_mappings: templateIncomeMappings
      });

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `affordability_export_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.msg || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  // Build AI chat context based on current state
  const aiChatContext = useMemo(() => {
    // Determine what fields are missing based on current step
    const missingRequired = [];
    if (!incomes.some(i => i.gross_annual > 0)) {
      missingRequired.push('income');
    }
    if (activeStep >= 2 && lockedCategoryIds.length === 0) {
      missingRequired.push('locked_categories');
    }

    // Context is complete when we have scenario results
    const isComplete = activeStep >= 5 && scenarioResult !== null;

    // Build locked categories with full details (name, amount)
    const lockedCategoriesWithDetails = [];
    categoriesData.forEach(account => {
      (account.categories || []).forEach(cat => {
        if (lockedCategoryIds.includes(cat.id)) {
          lockedCategoriesWithDetails.push({
            name: cat.name,
            account: account.name,
            monthly_amount: cat.monthly_allocation || 0
          });
        }
      });
    });

    // Build current values from state
    const currentValues = {
      step: activeStep,
      step_name: steps[activeStep],
      baseline_state: baselineState,
      scenario_state: scenarioState,
      income_count: incomes.filter(i => i.gross_annual > 0).length,
      total_gross_income: incomes.reduce((sum, i) => sum + (i.gross_annual || 0), 0),
      incomes: incomes.map(i => ({
        label: i.label,
        gross_annual: i.gross_annual,
        type: i.type,
        frequency: i.frequency
      })),
      locked_category_count: lockedCategoryIds.length,
      locked_categories: lockedCategoriesWithDetails,
      locked_categories_total_monthly: lockedCategoriesWithDetails.reduce((sum, c) => sum + c.monthly_amount, 0),
      factor_count: factors.length,
      factors: factors.map(f => ({
        type: f.factor_type,
        label: f.label,
        monthly_amount: f.monthly_amount
      })),
      has_allocation_overrides: Object.keys(allocationOverrides).length > 0,
    };

    // Add computed results if available
    let computedResults = null;
    if (scenarioResult) {
      computedResults = {
        baseline_post_tax_income: scenarioResult.baseline?.post_tax_income_monthly,
        scenario_post_tax_income: scenarioResult.scenario?.post_tax_income_monthly,
        baseline_locked_total: scenarioResult.baseline?.locked_total,
        scenario_locked_total: scenarioResult.scenario?.locked_total,
        baseline_flexible_amount: scenarioResult.baseline?.flexible_amount,
        scenario_flexible_amount: scenarioResult.scenario?.flexible_amount,
        delta_post_tax: scenarioResult.comparison?.delta_post_tax_income,
        delta_locked: scenarioResult.comparison?.delta_locked,
        delta_flexible: scenarioResult.comparison?.delta_flexible,
        net_impact: scenarioResult.comparison?.net_flexible_impact,
        state_changed: baselineState !== scenarioState,
      };
    }

    // Build workflow definition with steps and their editable fields
    const workflow = {
      current_step: activeStep,
      total_steps: steps.length,
      steps: [
        {
          index: 0,
          name: 'Configure Baseline',
          description: 'Set current state of residence and add income sources with gross annual amounts',
          fields: ['baseline_state', 'incomes'],
        },
        {
          index: 1,
          name: 'Link Templates',
          description: 'Connect existing split templates to income sources for accurate frequency calculations',
          fields: ['template_income_mappings'],
        },
        {
          index: 2,
          name: 'Select Categories',
          description: 'Choose which expense categories are fixed (locked) vs flexible',
          fields: ['locked_categories'],
        },
        {
          index: 3,
          name: 'Define Scenario',
          description: 'Set the hypothetical state and add life-change factors to model',
          fields: ['scenario_state', 'factors'],
        },
        {
          index: 4,
          name: 'Adjust Allocations',
          description: 'Fine-tune allocation percentages for the scenario',
          fields: ['allocation_overrides'],
        },
        {
          index: 5,
          name: 'Review Results',
          description: 'Compare baseline vs scenario outcomes and understand the financial impact',
          fields: [], // Read-only step - no editable fields
        },
      ],
    };

    return {
      page: 'affordability_explorer',
      purpose: 'Explore how life changes (moving states, new job, rent changes) impact your financial allocations',
      workflow,
      is_complete: isComplete,
      missing_required: missingRequired,
      current_values: currentValues,
      computed_results: computedResults,
      schema_fields: {
        baseline_state: {
          name: 'baseline_state',
          description: 'Current state of residence for tax calculations',
          field_type: 'select',
          required: true,
        },
        incomes: {
          name: 'incomes',
          description: 'List of income sources with gross annual amounts and frequencies',
          field_type: 'array',
          required: true,
        },
        template_income_mappings: {
          name: 'template_income_mappings',
          description: 'Mapping of split templates to income sources',
          field_type: 'object',
          required: false,
        },
        locked_categories: {
          name: 'locked_categories',
          description: 'Categories with fixed monthly allocations that cannot be reallocated',
          field_type: 'multi-select',
          required: false,
        },
        scenario_state: {
          name: 'scenario_state',
          description: 'Hypothetical state for scenario comparison',
          field_type: 'select',
          required: false,
        },
        factors: {
          name: 'factors',
          description: 'Life change factors to model (rent change, income change, moving states)',
          field_type: 'array',
          required: false,
        },
        allocation_overrides: {
          name: 'allocation_overrides',
          description: 'Manual adjustments to allocation percentages',
          field_type: 'object',
          required: false,
        },
      },
    };
  }, [
    activeStep,
    baselineState,
    scenarioState,
    incomes,
    lockedCategoryIds,
    categoriesData,
    factors,
    allocationOverrides,
    scenarioResult,
  ]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <BaselineIncomeConfig
            state={baselineState}
            setState={(s) => {
              setBaselineState(s);
              setScenarioState(s); // Default scenario to same state
            }}
            incomes={incomes}
            setIncomes={setIncomes}
            baselineResult={baselineResult}
            setBaselineResult={setBaselineResult}
          />
        );
      case 1:
        return (
          <TemplateIncomeLinker
            incomes={incomes}
            templateIncomeMappings={templateIncomeMappings}
            setTemplateIncomeMappings={setTemplateIncomeMappings}
          />
        );
      case 2:
        return (
          <CategoryLockSelector
            categoriesData={categoriesData}
            lockedCategoryIds={lockedCategoryIds}
            setLockedCategoryIds={setLockedCategoryIds}
          />
        );
      case 3:
        return (
          <ScenarioFactors
            baselineState={baselineState}
            scenarioState={scenarioState}
            setScenarioState={setScenarioState}
            factors={factors}
            setFactors={setFactors}
            baselineResult={baselineResult}
            incomes={incomes}
          />
        );
      case 4:
        return (
          <AllocationAdjuster
            scenarioResult={scenarioResult}
            allocationOverrides={allocationOverrides}
            setAllocationOverrides={setAllocationOverrides}
            calculating={calculating}
          />
        );
      case 5:
        return (
          <ScenarioComparison
            scenarioResult={scenarioResult}
            calculating={calculating}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
      <Typography variant="h4" gutterBottom>
        Affordability Explorer
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Explore how life changes would impact your financial allocations
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        {/* Mobile: Show simple step indicator */}
        {isMobile ? (
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="overline" color="text.secondary">
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography variant="h6">
              {steps[activeStep]}
            </Typography>
          </Box>
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>

          {/* Mobile: AI Chat button inline */}
          {isMobile && (
            <Tooltip title={aiChatContext?.is_complete ? 'Chat about your scenario' : 'Get help'}>
              <IconButton
                onClick={() => setMobileAIChatOpen(true)}
                sx={{
                  bgcolor: aiChatContext?.is_complete ? 'primary.main' : 'grey.200',
                  color: aiChatContext?.is_complete ? 'white' : 'text.secondary',
                  '&:hover': {
                    bgcolor: aiChatContext?.is_complete ? 'primary.dark' : 'grey.300',
                  },
                }}
              >
                {aiChatContext?.is_complete ? <BotIcon /> : <HelpIcon />}
              </IconButton>
            </Tooltip>
          )}

          <Box>
            {activeStep === steps.length - 1 ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleExport}
                  disabled={exporting || calculating || !scenarioResult}
                >
                  {exporting ? 'Exporting…' : 'Export JSON'}
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  Start Over
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={calculating}
              >
                {activeStep === steps.length - 2 ? 'View Results' : 'Next'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Desktop: Floating AI Chat Button */}
      {!isMobile && (
        <AIChatButton
          context={aiChatContext}
          disabled={loading}
        />
      )}

      {/* Mobile: AI Chat Modal (triggered from inline button) */}
      {isMobile && (
        <AIChatModal
          open={mobileAIChatOpen}
          onClose={() => setMobileAIChatOpen(false)}
          context={aiChatContext}
        />
      )}
    </Container>
  );
}

export default AffordabilityExplorerPage;
